import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { Input } from 'src/components/ConnectedForm'
import { useCreatePage, useUpdateOrganization, useUpdatePage } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'

const CustomAppCreate = ({ onCancelButtonClick, onCreate }) => {
   const [error, setError] = useState(null)
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)

   const { createPage } = useCreatePage()
   const { updatePage } = useUpdatePage()
   const { updateOrganization } = useUpdateOrganization({
      variables: {
         withGlobalNavLinksTargetMetadata: true
      }
   })

   const handleCreateCustomApp = async (values, actions) => {
      try {
         const result = await createPage(values)

         const newPage = result.data.createPage

         // TODO: The UX process of "publishing" a page (enabling an app)
         // still needs to be fleshed out. This is highly subject to change
         // For now we manually do a page update to set page as published
         // (since we cannot do this though the create mutation because
         // of what the API currently allows for input)
         // We do this for now as a page being published emulates an app being "enabled"
         // PR #692
         await updatePage({ id: newPage.id }, { published: true })
         // TODO: For now, while pages are pretending to be custom apps,
         // We manually sync updates to globalNavLinks
         // PR #692
         await updateOrganization({
            globalNavLinks: [{
               operation: 'createInternalLink',
               createInternalLink: {
                  name: newPage.title,
                  pageId: newPage.id
               }
            }]
         })

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })

         onCreate()
      } catch (err) {
         setError(err)
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <View>
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="15px"
            borderBottom="1px solid #eee">
            <Text size="2" weight="medium">
               Create Custom App
            </Text>
         </View>
         <Formik
            initialValues={{ title: '' }}
            onSubmit={handleCreateCustomApp}
            render={({ handleSubmit, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  onSubmit={handleSubmit}>
                  {error && (
                     <NotificationInline
                        borderRadius="3px"
                        textSize="2"
                        type="error"
                        title="Error"
                        message={error.graphQLErrors.map(e => e.message).join(', ') || error.message}
                     />
                  )}
                  <View padding="15px">
                     <View>
                        <Text size="1.5" color="#444">
                           Please enter a name for this app
                        </Text>
                        <Spacer size="2" />
                        <Input name="title" size="lg" placeholder="App name" />
                     </View>
                     <Spacer size="3" />
                     <View display="flex" justifyContent="space-between">
                        <Button
                           width="48%"
                           onClick={onCancelButtonClick}
                           name="Cancel"
                           color="#fff"
                           textColor="#666"
                           size="md"
                           textSize="0.9"
                        />
                        <Button
                           width="50%"
                           type="submit"
                           name="Create"
                           color={theme.protoSettingsHighlightBackgroundColor}
                           textColor={theme.protoSettingsHighlightForegroundColor}
                           textWeight="medium"
                           size="md"
                           textSize="0.9"
                           mode={isSubmitting ? 'loading' : 'normal'}
                        />
                     </View>
                  </View>
               </Form>
            )}
         />
      </View>
   )
}

CustomAppCreate.propTypes = {
   onCancelButtonClick: PropTypes.func.isRequired,
   onCreate: PropTypes.func.isRequired
}

export default CustomAppCreate
